/**
 * aichat.ts — the AI chat overlay's PURE logic: transcript, request shape, rendering.
 *
 * Andrei, 2026-07-29: *"add a button that can add an overlay AI agent chat window
 * where Bedrock opus 5 model can get access to the current chart data and APIs and
 * answer any questions, including searching for a trade setup, doing trade analysis
 * etc. No need to store data."*
 *
 * WHY THIS FILE IS SEPARATE FROM main.ts
 * --------------------------------------
 * `chartapp/README.md` records a ticket-state bug that "passed all four gates green,
 * because it lived in frontend state" — pytest cannot see `.ts`, and tsc/vite have
 * nothing to object to in wrong-but-well-typed logic. Gate 5 (vitest) exists for that
 * class. So everything here is a pure function over explicit arguments: no DOM, no
 * fetch, no module-level mutable state. `main.ts` owns the wiring; this owns the
 * decisions, and the decisions are what a test can pin.
 *
 * THE TRANSCRIPT LIVES HERE, NOT ON THE SERVER
 * --------------------------------------------
 * "No need to store data": the backend persists nothing and keeps no session, so the
 * client posts the FULL transcript each turn and owns it in memory. Consequences,
 * which are design and not oversight:
 *   * a page reload starts a fresh conversation;
 *   * nothing is written to localStorage — deliberately, so a trading conversation
 *     does not outlive the tab it was typed into;
 *   * `trimHistory` bounds what we post, mirroring the server's own MAX_MESSAGES.
 */

/** One turn as the user sees it. `pending` marks the optimistic row while in flight. */
export type ChatMsg = {
  role: 'user' | 'assistant';
  text: string;
  pending?: boolean;
  error?: boolean;
  /** Which ict/ tools produced this answer — shown so a claim is auditable. */
  tools?: ToolCall[];
  usage?: { inputTokens?: number; outputTokens?: number; totalTokens?: number };
};

export type ToolCall = {
  tool: string;
  input?: Record<string, unknown>;
  ok?: boolean;
  error?: string | null;
};

export type AgentReply = {
  reply: string;
  tool_calls?: ToolCall[];
  rounds?: number;
  usage?: ChatMsg['usage'];
  stop_reason?: string;
  model_id?: string;
};

export type AgentHealth = {
  ok: boolean;
  model_id?: string;
  region?: string;
  tools?: string[];
  writes_possible?: boolean;
  credential_method?: string;
  error?: string;
};

/** The chart view the agent is told about. Mirrors the backend's `_chart_context`. */
export type ChartContext = {
  asset?: string;
  tf?: string;
  source?: string;
  overlays_on?: string[];
  bars_count?: number;
  selected_bar?: number | null;
};

/**
 * Which overlays are ON, read from the pane's own overlay record.
 *
 * THE PANE IS THE ONE AUTHORITATIVE SOURCE. This used to be
 * `OVERLAYS.filter(o => document.getElementById(`ov-${o.id}`)?.checked)` in `main.ts`, against
 * the SHARED sidebar checkbox ids that were deleted on 2026-07-31 when overlays became per-pane
 * (`renderOverlayToggles` now writes `innerHTML = ''`; `panelayout.test.ts:53` and
 * `overlaydefaults.test.ts:188` pin that deletion). `getElementById` on a dead id returns null,
 * so the filter silently yielded `[]` and the model was told EVERY overlay was off -- which made
 * every answer it gave about the chart wrong, with no error anywhere.
 *
 * The keys come from the passed object, NOT from a re-derivation against `OVERLAYS` or a second
 * copy of `DEFAULT_OVERLAYS`: a duplicated constant is how this repo hides a wrong value behind a
 * green suite. Caller hands in `panes.get(EXECUTION_PANE_ID).overlays`, the same record the
 * per-pane checkboxes write to and `savePaneState` persists.
 */
export function overlaysOn(overlays: Record<string, boolean> | null | undefined): string[] {
  if (!overlays) return [];
  return Object.keys(overlays).filter((k) => overlays[k] === true);
}

/**
 * Must match `agent.MAX_MESSAGES`. Kept in sync by a test that reads the Python
 * constant, not by hope: if the server silently truncated more than the client
 * expected, the user would watch the top of their conversation stop being answered
 * with no indication why.
 */
export const MAX_HISTORY = 60;

/** Keep the most recent N turns — the same end of the list the server keeps. */
export function trimHistory(msgs: ChatMsg[], max: number = MAX_HISTORY): ChatMsg[] {
  return msgs.length <= max ? msgs.slice() : msgs.slice(msgs.length - max);
}

/**
 * Build the POST body for /api/agent/chat.
 *
 * Only `{role, text}` is sent. Tool traffic is deliberately NOT echoed back: the
 * server drops client-supplied tool blocks (a client could otherwise fabricate tool
 * output and put words in the model's mouth), so sending them would be dead weight
 * that also implied a trust we do not have.
 *
 * `pending` and `error` rows are excluded — an in-flight placeholder is not a turn,
 * and posting a failed row would ask the model to answer an error message.
 */
export function buildRequest(
  msgs: ChatMsg[],
  chart: ChartContext | null,
): { messages: { role: string; text: string }[]; chart?: ChartContext } {
  const messages = trimHistory(msgs)
    .filter((m) => !m.pending && !m.error && m.text.trim() !== '')
    .map((m) => ({ role: m.role, text: m.text }));
  const body: { messages: { role: string; text: string }[]; chart?: ChartContext } = { messages };
  if (chart && Object.keys(chart).length > 0) body.chart = chart;
  return body;
}

/** Is there anything worth sending? Guards the send button and the Enter key. */
export function canSend(draft: string, busy: boolean, healthy: boolean): boolean {
  return healthy && !busy && draft.trim().length > 0;
}

/**
 * Fold a successful reply into the transcript, replacing the pending row.
 *
 * An empty `reply` becomes a visible, explanatory message rather than a blank
 * bubble: the model hitting `max_tokens` mid-thought returns text-less content, and a
 * silent empty bubble reads as "the app is broken" instead of "the answer was cut
 * off". Observed for real — an unfiltered `list_assets` truncated at max_tokens.
 */
export function applyReply(msgs: ChatMsg[], reply: AgentReply): ChatMsg[] {
  const out = msgs.filter((m) => !m.pending);
  let text = (reply.reply ?? '').trim();
  if (!text) {
    text =
      reply.stop_reason === 'max_tokens'
        ? '(the answer hit the output limit before any text was produced — ask something narrower)'
        : `(no text returned; stop_reason=${reply.stop_reason ?? 'unknown'})`;
  }
  out.push({
    role: 'assistant',
    text,
    tools: reply.tool_calls ?? [],
    usage: reply.usage,
  });
  return out;
}

/** Fold a failure into the transcript. The message is kept — the user may retry. */
export function applyError(msgs: ChatMsg[], detail: string): ChatMsg[] {
  const out = msgs.filter((m) => !m.pending);
  out.push({ role: 'assistant', text: detail, error: true });
  return out;
}

/**
 * One-line provenance for an answer: which `ict/` tools ran, and whether any failed.
 *
 * This is not decoration. `.hermes.md` §0b.1 — "primary source beats report" — is why
 * an AI claim about a chart has to show what produced it; an answer with NO tool calls
 * is a model talking from memory, and the UI must make that visible rather than let it
 * pass as computed.
 */
export function toolSummary(tools: ToolCall[] | undefined): string {
  if (!tools || tools.length === 0) return 'no tools called — answered without reading chart data';
  const counts = new Map<string, { n: number; failed: number }>();
  for (const t of tools) {
    const key = t.tool + (t.input?.component ? `:${t.input.component}` : '');
    const cur = counts.get(key) ?? { n: 0, failed: 0 };
    cur.n += 1;
    if (t.ok === false) cur.failed += 1;
    counts.set(key, cur);
  }
  return [...counts.entries()]
    .map(([k, v]) => `${k}${v.n > 1 ? `×${v.n}` : ''}${v.failed ? ' (failed)' : ''}`)
    .join(', ');
}

/** Why the chat is unavailable, in words the user can act on. */
export function healthNote(h: AgentHealth | null): string {
  if (!h) return 'checking Bedrock availability…';
  if (h.ok) {
    const parts = [h.model_id ?? 'model', h.region ?? ''].filter(Boolean);
    return `${parts.join(' · ')} · read-only`;
  }
  return h.error ?? 'Bedrock unavailable';
}

/**
 * Minimal, SAFE markdown → HTML. Escapes first, then re-introduces a known set of tags.
 *
 * Escaping is not optional and not theatre: this renders text produced by a model that
 * has just read tool output, and tool output includes strings from an exchange API. A
 * naive `innerHTML` of that is a script-injection path into a page that can place
 * orders in its other half. So `escapeHtml` runs FIRST and the inline rules only ever
 * match on already-escaped text.
 *
 * Supported: fenced code, inline code, bold, headings, bullets, tables, blank-line
 * paragraphs. Deliberately NOT supported: links and images — an `<a href>` minted from
 * model output is a phishing surface with no upside in a local analysis tool.
 */
export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function inline(s: string): string {
  return s
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
}

export function renderMarkdown(src: string): string {
  const esc = escapeHtml(src);
  const out: string[] = [];
  let inCode = false;
  let inList = false;
  let inTable = false;
  const closeList = () => { if (inList) { out.push('</ul>'); inList = false; } };
  const closeTable = () => { if (inTable) { out.push('</table>'); inTable = false; } };

  for (const raw of esc.split('\n')) {
    const line = raw.trimEnd();
    if (line.startsWith('```')) {
      closeList(); closeTable();
      out.push(inCode ? '</pre>' : '<pre>');
      inCode = !inCode;
      continue;
    }
    if (inCode) { out.push(line); continue; }

    // A table separator row (|---|---|) is structure, not content.
    if (/^\|[\s|:-]+\|$/.test(line)) continue;

    if (line.startsWith('|') && line.endsWith('|')) {
      closeList();
      if (!inTable) { out.push('<table>'); inTable = true; }
      const cells = line.slice(1, -1).split('|').map((c) => inline(c.trim()));
      out.push('<tr>' + cells.map((c) => `<td>${c}</td>`).join('') + '</tr>');
      continue;
    }
    closeTable();

    const h = line.match(/^(#{1,4})\s+(.*)$/);
    if (h) { closeList(); out.push(`<h4>${inline(h[2])}</h4>`); continue; }

    const li = line.match(/^\s*[-*]\s+(.*)$/);
    if (li) {
      if (!inList) { out.push('<ul>'); inList = true; }
      out.push(`<li>${inline(li[1])}</li>`);
      continue;
    }
    closeList();

    if (line.trim() === '') { out.push(''); continue; }
    out.push(`<p>${inline(line)}</p>`);
  }
  if (inCode) out.push('</pre>');
  closeList(); closeTable();
  return out.join('\n');
}
